/**
 * Centrālie enum tipi (statusi, lomas, kategorijas). Koplietoti starp frontend un backend.
 * Avots: AmberNord_B2B_Build_Prompt.md §1, §5.
 */

export const MARKETS = ['eu', 'ch'] as const;
export type Market = (typeof MARKETS)[number];

export const TENANT_TYPES = ['platform', 'supplier', 'customer', 'carrier'] as const;
export type TenantType = (typeof TENANT_TYPES)[number];

export const LOCALES = ['en', 'de', 'lv', 'lt'] as const;
export type Locale = (typeof LOCALES)[number];

/** RBAC lomas. Lauku līmeņa redakciju kontrolē atļauju matrica (packages/shared/src/rbac.ts S2). */
export const ROLES = [
  // platform_super = "super admin" — redz visu, visas atļaujas (testēšanai/operācijām).
  // Sākas ar `platform_`, tāpēc visi tenant-scoping `isPlatform` testi to ietver automātiski.
  'platform_super',
  'platform_admin',
  'platform_ops',
  'platform_finance',
  'supplier_admin',
  'supplier_ops',
  'customer_admin',
  'customer_buyer',
  // carrier = 4th party (§ automation plan D-carrier). Transporta izpildītājs.
  'carrier_admin',
  'carrier_ops',
  'auditor',
] as const;
export type Role = (typeof ROLES)[number];

export const USER_STATUSES = ['pending', 'active', 'suspended', 'disabled'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** Produktu kategorijas (5 sākotnējās) — §1. */
export const PRODUCT_CATEGORIES = [
  'nfc_juice', // 100% NFC smiltsērkšķu sula
  'seed_oil', // sēklu eļļa
  'pulp_oil', // mīkstuma eļļa (Omega-7)
  'lyophilized_powder', // liofilizēts pulveris
  'dry_pomace', // sausās spiedpaliekas
] as const;
export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

/**
 * Pasūtījuma dzīves cikla stāvokļi (Cloudflare Workflow stāvokļu automāts — S4 §8).
 * placed→accepted→awaiting_confirmation→confirmed→production→labelled→loaded→
 * dispatched→delivered→inspected→invoiced→paid (+ cancelled).
 */
export const ORDER_STATUSES = [
  // Pre-placed RFQ/offer fāze (automation plan Phase 2). Klients pieprasa cenu →
  // b2b admin komponē un nosūta piedāvājumu → klients apstiprina → 'placed'.
  'quote_requested',
  'offer_drafted',
  'offer_sent',
  'offer_approved',
  'placed',
  'accepted',
  'awaiting_confirmation',
  'confirmed',
  'production',
  'labelled',
  'loaded',
  'dispatched',
  'delivered',
  'inspected',
  'invoiced',
  'paid',
  'cancelled',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** Milestone statuss, ko redz gala klients (§7 — apkopots, ne iekšējais). */
export const CUSTOMER_MILESTONES = [
  'quoted',
  'placed',
  'accepted',
  'in_production',
  'shipped',
  'delivered',
] as const;
export type CustomerMilestone = (typeof CUSTOMER_MILESTONES)[number];

export const INCOTERMS = ['FCA', 'FCA_loading', 'DAP', 'DDP'] as const;
export type Incoterm = (typeof INCOTERMS)[number];

/**
 * Piegādes režīms (automation plan D2). Nosaka §7 anonimizāciju:
 * - `crossdock`: sūtījums iet caur CH crossdock / Matisons kā saņēmējs → gala klients SLĒPTS.
 * - `direct`: tiešā piegāde uz klienta rampu → reālā klienta adrese REDZAMA piegādātājam/pārvadātājam.
 */
export const SHIPPING_MODES = ['crossdock', 'direct'] as const;
export type ShippingMode = (typeof SHIPPING_MODES)[number];

/**
 * Izmaksu komponenšu veidi (automation plan D5 — viens aprēķins, projicēts pēc auditorijas).
 * `margin` NEKAD nav redzama gala klientam zem open-book (§4.9).
 */
export const COST_COMPONENT_KINDS = [
  'product',
  'transport',
  'customs',
  'insurance',
  'margin',
  'tax',
  'admin',
] as const;
export type CostComponentKind = (typeof COST_COMPONENT_KINDS)[number];

/** Pārvadātāja transporta veidi (carrier profils). */
export const VEHICLE_TYPES = ['van', 'truck', 'reefer', 'container', 'other'] as const;
export type VehicleType = (typeof VEHICLE_TYPES)[number];

/** Kam pieder profila noteikumi (tenantTerms): komerciālie + juridiskie. */
export const PROFILE_KINDS = ['customer', 'supplier', 'carrier'] as const;
export type ProfileKind = (typeof PROFILE_KINDS)[number];

export const DECLARATION_TYPES = [
  'GMO',
  'Allergen',
  'BSE_TSE',
  'NonIrrad',
  'ETO',
  'Vegan',
  'Kosher_Halal',
  'FCM_DoC',
] as const;
export type DeclarationType = (typeof DECLARATION_TYPES)[number];

export const DOCUMENT_TYPES = [
  'CoA',
  'Invoice',
  'PackingList',
  'OriginDecl',
  'EUR1_REX',
  'Intrastat',
  'Declaration',
  'Other',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const INVOICE_KINDS = ['supplier_to_platform', 'platform_to_customer'] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];

export const CLAIM_TYPES = ['visible', 'latent'] as const;
export type ClaimType = (typeof CLAIM_TYPES)[number];

export const COA_RESULT_STATUSES = ['PASS', 'FAIL'] as const;
export type CoAResultStatus = (typeof COA_RESULT_STATUSES)[number];

export const NOTICE_KINDS = ['operational', 'formal', 'time_critical'] as const;
export type NoticeKind = (typeof NOTICE_KINDS)[number];

export const TASK_CHANNELS = ['inapp', 'email', 'push'] as const;
export type TaskChannel = (typeof TASK_CHANNELS)[number];

export const CERTIFICATE_TYPES = ['BIO', 'Insurance', 'Compliance', 'Declaration'] as const;
export type CertificateType = (typeof CERTIFICATE_TYPES)[number];

export const DSR_TYPES = [
  'access',
  'rectification',
  'erasure',
  'portability',
  'objection',
] as const;
export type DsrType = (typeof DSR_TYPES)[number];
